import { swymFetch } from '~/services/fetch-swym'

import { markAsRead as markCommunityAsRead } from '~/entities/container/community/api/communities'
import { communityParser } from '~/entities/container/community/api/parser-community'
import type { CreateCommunityData, ICommunity } from '~/entities/container/community/interfaces/community'
import { conversationParser } from '~/entities/container/conversation/api/parser-conversation'
import type { IConversation, IConversationMark } from '~/entities/container/conversation/interfaces/conversation'
import { _userParser } from '~/entities/user/user/api/parser-users'

import { timelineParser } from '~/features-shared/content/api/parser-contents'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

import type { SwymResponse } from '~/interfaces/services-dto'

// import { communityParser } from '~/entities/container/community/api/parser-community'

export function getConversations() {
  return swymFetch
    .get('/api/directmessages/lite')
    .json<SwymResponse<IConversation[]>>()
    .then((response) => {
      response.result.forEach((el) => {
      // eslint-disable-next-line max-nested-callbacks
        el.users.map(u => _userParser(u))
      })

      return response
    })
}

/**
 * Fetches the marks data of the conversations
 *
 * @returns The marks data
 */
export function getConversationsMarks() {
  return swymFetch
    .get('/api/directmessages/lite/marks')
    .json<SwymResponse<{ communities: IConversationMark[] }>>()
}

export function getConversation(id: string) {
  return swymFetch
    .get(`/api/directmessages/${id}`, { searchParams: { as_admin: false, id, return_6wtags: false } })
    .json<SwymResponse<Record<string, string>>>()
    .then(conversationParser)
}

export function createConversation(users: string[], topic?: string): Promise<SwymResponse<IConversation>> {
  return swymFetch
    .post('/api/directmessages', {
      json: {
        users,
        ...(topic ? { topic } : undefined),
      },
    })
    .json<SwymResponse<Record<string, string>>>()
    .then(conversationParser)
    .catch((e) => {
    // Already created conversation
      if (e.response.status === 409)
        return conversationParser(e.data)
      // return e.response.json().then(conversationParser)
      else throw new Error(e)
    })
}

export function addMembersConversation(id: string, users: string[]) {
  return swymFetch
    .patch(`/api/directmessages/addmembers/${id}`, { json: { member_ids: users } })
    .json<SwymResponse<Record<string, string>>>()
    .then(conversationParser)
}

export function removeMembersConversation(id: string, users: string[]) {
  return swymFetch
    .patch(`/api/directmessages/removemembers/${id}`, { json: { member_ids: users } })
    .json<SwymResponse<Record<string, string>>>()
    .then(conversationParser)
}

export const markAsRead = markCommunityAsRead

export function hideConversation(conversation_id: string, hide = true) {
  return swymFetch
    .patch(`/api/directmessages/${conversation_id}`, {
      json: { hide },
    })
    .json<SwymResponse<IConversation>>()
}

export function setTopic(conversation_id: string, topic: string) {
  return swymFetch
    .patch(`/api/directmessages/settopic/${conversation_id}`, {
      json: { topic },
    })
    .json<SwymResponse<IConversation>>()
}

export function setPersistency(conversation_id: string, is_persistent: boolean) {
  return swymFetch
    .patch(`/api/directmessages/setpersistency/${conversation_id}`, {
      json: { is_persistent },
    })
    .json<SwymResponse<IConversation>>()
}
export function transformDmToCommunity(data: CreateCommunityData, conversation_id: string) {
  return swymFetch
    .post(`/api/community/dmToCommunity/${conversation_id}`, {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunity>>()
    .then(communityParser)
}

export function transformRTConvToCommunity(data: CreateCommunityData, conversation_id: string) {
  return swymFetch
    .post(`/api/externalDM/rtconvToCommunity/${conversation_id}`, {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunity>>()
    .then(communityParser)
}

function _getLastMessage(message_id: string) {
  return swymFetch.get(`/api/community/timeline/messages/${message_id}`)
    .json<SwymResponse<ITimeline[]>>()
}
// .then(result => timelineParser(result))

export const getLastMessage = mutualizerDecorator<
  typeof _getLastMessage,
  string
>({
  fetch: _getLastMessage,
  merge: bucketList => bucketList.join(','),
  resolve: ([response, [, resolve]]) => resolve(timelineParser(response)),
})

export function getConversationIfExists(login: string, unhide = false) {
  return swymFetch
    .get(`/api/directmessages/private/?login=${login}&unhide=${unhide}`)
    .json<SwymResponse<Record<string, string>>>()
    .then(conversationParser)
}

/**
 * The API that should be called to let the Swym backend know that there has been an event from RTC server that should be stored in swym backend
 *
 * @example
 * // A catia collab event in a swym conversation
 *
 * @param data The required data for the API call
 * @param data.convId The id of the conversation
 * @param data.serviceId The event related to co-review that we receive from RTC server
 * @param data.msgId The id of the message in RTC server
 * @param data.type The type of event. 'catiaCollab' is the default
 * @param data.users Array of user logins, if applicable
 */
export function logAVCall(data: { convId: string, serviceId: string, msgId: string, type?: string, users?: string[] }) {
  return swymFetch
    .post(`/api/directmessages/logavcall/${data.convId}`, {
      json: {
        date_start: Date.now(),
        serviceId: data.serviceId,
        msgId: data.msgId,
        type: data.type ?? 'catiaCollab',
        users: data.users ?? [],
      },
    })
}
